<template>
    <b-row>
        <b-col lg="4" md="4">
            <b-form-group
                :label="$t('Component(Label):organization')"
                label-for="organization"
            >
                <div v-if="isManagable">
                    <v-select
                        v-model="organization"
                        id="organization"
                        label="title"
                        :clearable="false"
                        @input="emitPermissionChange"
                        :placeholder="$t('Component(Placeholder):organization')"
                        :options="organizations"
                    />
                    <small v-if="isValidated && !organization" class="text-danger">
                        {{ $t('Component(Label):organization') }} can't be empty
                    </small>
                </div>
                <div v-else class="d-flex align-items-center">
                    <span class="mt-50">{{ organization.title }}</span>
                </div>
            </b-form-group>
        </b-col>
        <b-col lg="3" md="3">
            <b-form-group
                :label="$t('Component(Label):role')"
                label-for="role"
            >
                <div v-if="isManagable">
                    <v-select
                        v-model="role"
                        id="role"
                        label="title"
                        :clearable="false"
                        @input="emitPermissionChange"
                        :placeholder="$t('Component(Placeholder):role')"
                        :options="roles"
                    />
                    <small v-if="isValidated && !role" class="text-danger">
                        {{ $t('Component(Label):role') }} can't be empty
                    </small>
                </div>
                <div v-else class="d-flex align-items-center">
                    <span class="mt-50">{{ role.title }}</span>
                </div>
            </b-form-group>
        </b-col>
        <b-col lg="2" md="2">
            <div>
                <label :for="'validFrom' + index">{{ $t('Component(Label):valid-from') }}</label>
                <div v-if="isManagable">
                    <b-form-datepicker
                        :id="'validFrom' + index"
                        v-model="validFrom"
                        @input="emitPermissionChange"
                        :placeholder="$t('Component(Placeholder):valid-from')"
                        :locale="$store.state.component.datePicker.locale"
                        :date-format-options="$store.state.component.datePicker.format"
                        :initial-date="initialDate"
                    />
                </div>
                <div v-else>
                    <div class="mt-50">{{ renderDate(validFrom) }}</div>
                </div>
            </div>
        </b-col>
        <b-col lg="2" md="2">
            <div>
                <label :for="'validTo' + index">{{ $t('Component(Label):valid-to') }}</label>
                <b-form-datepicker
                    :id="'validTo' + index"
                    v-model="validTo"
                    @input="emitPermissionChange"
                    :placeholder="$t('Component(Placeholder):valid-to')"
                    :locale="$store.state.component.datePicker.locale"
                    reset-button
                    :reset-value="validToResetValue"
                    :date-format-options="$store.state.component.datePicker.format"
                    initial-date="2099-01-01"
                />
                <small v-if="validToLessThenValidFrom" class="text-danger">
                    {{ $t('Component(Label):valid-to') }} can't be less then {{ $t('Component(Label):valid-from') }}
                </small>
            </div>
        </b-col>
        <b-col lg="1" md="1" class="d-flex justify-content-end">
            <div class="my-auto align-items-center">
                <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="danger"
                    class="btn-icon rounded-circle"
                    v-if="showDeleteButton"
                    @click="remove"
                >
                    <feather-icon icon="MinusIcon" />
                </b-button>
            </div>
        </b-col>
    </b-row>
</template>
<script>
import {
    BButton, BRow, BCol, BFormGroup, BFormDatepicker,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import dateRenderer from "@core/mixins/date-renderer";

export default {
    mixins: [dateRenderer],
    components: {
        BRow, BCol, vSelect, BFormGroup, BFormDatepicker, BButton,
    },
    directives: {
        Ripple
    },
    props: {
        organizations: {
            type: Array,
            default() {
                return [];
            }
        },
        roles: {
            type: Array,
            default() {
                return [];
            }
        },
        permission: {
            type: Object,
            default() {
                return {
                    organization: null,
                    role: null,
                    validFrom: null,
                    validTo: '2099-01-01'
                }
            }
        },
        index: {
            type: Number,
            required: true
        },
        showDeleteButton: {
            type: Boolean,
            default: true
        },
        isManagable: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            organization: this.permission.organization,
            role: this.permission.role,
            validFrom: this.permission.validFrom,
            validTo: this.permission.validTo ?? new Date('2099-01-01'),
            initialDate: new Date(),

            isValidated: false,
            validToLessThenValidFrom: false
        };
    },
    methods: {
        remove() {
            this.$emit('remove', this.permission, this.index)
        },
        isValid() {
            this.isValidated = true;
            this.validToLessThenValidFrom = false;

            if (!this.organization || !this.role) {
                return false;
            }

            if (this.validFrom) {
                if (this.validTo && !this.$moment(this.validTo).isAfter(this.validFrom)) {
                    this.validToLessThenValidFrom = true;
                    return false;
                }
            }

            return true;
        },
        emitPermissionChange() {
            this.$emit('change', {
                organization: this.organization,
                role: this.role,
                validFrom: this.validFrom,
                validTo: this.validTo,
            }, this.index);
        }
    },
    computed: {
        validToResetValue() {
            if (this.validFrom) {
                return this.$moment(this.validFrom).add(1, 'M').format('YYYY-MM-DD');
            }

            return this.$moment(this.initialDate).add(1, 'M').format('YYYY-MM-DD');
        },
    },
    watch: {
        permission(permission) {
            this.organization = permission.organization;
            this.role = permission.role;
            this.validFrom = permission.validFrom;
            this.validTo = permission.validTo;
        }
    }
}
</script>
